"use client"

import { useEffect, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import { Button } from "@medusajs/ui"
import { HttpTypes } from "@medusajs/types"
import Modal from "@modules/common/components/modal"
import Input from "@modules/common/components/input"
import useToggleState from "@lib/hooks/use-toggle-state"
import { getOfferConfig, createOffer } from "@lib/data/offers"
import { convertToLocale } from "@lib/util/money"
import { GetVariantOfferConfigResponse } from "@3dot3/shared-types"

type MakeOfferProps = {
  variant: HttpTypes.StoreProductVariant | undefined
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  customer: HttpTypes.StoreCustomer | null
}

export default function MakeOffer({ variant, product, region, customer }: MakeOfferProps) {
  const router = useRouter()
  const { countryCode } = useParams()
  const { state: isOpen, open, close } = useToggleState()

  const [offerConfig, setOfferConfig] = useState<GetVariantOfferConfigResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [offerAmount, setOfferAmount] = useState("")
  const [message, setMessage] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Get the variant price
  const variantPrice = variant?.calculated_price?.calculated_amount || 0
  const currencyCode = variant?.calculated_price?.currency_code || "usd"

  // Fetch offer config when variant changes
  useEffect(() => {
    const fetchConfig = async () => {
      if (!variant?.id) {
        setOfferConfig(null)
        return
      }

      setIsLoading(true)
      const config = await getOfferConfig(variant.id)
      setOfferConfig(config)
      setIsLoading(false)
    }

    fetchConfig()
  }, [variant?.id])

  // Don't show button if offers not enabled or still loading
  if (isLoading || !offerConfig?.offers_enabled || !variant) {
    return null
  }

  const handleOpenModal = () => {
    // Check if user is logged in
    if (!customer) {
      router.push(`/${countryCode}/account?redirect=${encodeURIComponent(window.location.pathname)}`)
      return
    }

    // Check if user has an address
    if (!customer.addresses || customer.addresses.length === 0) {
      setError("You need to add a shipping address before making an offer.")
      open()
      return
    }

    setError(null)
    setSuccessMessage(null)
    setOfferAmount("")
    setMessage("")
    open()
  }

  const validateOffer = (amount: number): string | null => {
    if (amount <= 0) {
      return "Offer must be greater than zero"
    }

    const percentage = (amount / variantPrice) * 100

    if (offerConfig.min_offer_percentage && percentage < offerConfig.min_offer_percentage) {
      const minAmount = (variantPrice * offerConfig.min_offer_percentage) / 100
      return `Offer must be at least ${convertToLocale({ amount: minAmount, currency_code: currencyCode })} (${offerConfig.min_offer_percentage}% of price)`
    }

    if (offerConfig.max_offer_percentage && percentage > offerConfig.max_offer_percentage) {
      const maxAmount = (variantPrice * offerConfig.max_offer_percentage) / 100
      return `Offer cannot exceed ${convertToLocale({ amount: maxAmount, currency_code: currencyCode })} (${offerConfig.max_offer_percentage}% of price)`
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    const amount = parseFloat(offerAmount)
    if (isNaN(amount)) {
      setError("Please enter a valid amount")
      return
    }

    // Validate using the same unit as variantPrice (base currency unit)
    const validationError = validateOffer(amount)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)

    // Send amount in base currency units (same as variant price)
    const result = await createOffer({
      variant_id: variant.id,
      offered_amount: amount,
      quantity: 1,
      region_id: region.id,
      message: message || undefined,
    })

    setIsSubmitting(false)

    if (!result.success) {
      setError(result.error || "Failed to submit offer")
      return
    }

    // Handle different statuses
    if (result.offer?.status === "accepted") {
      setSuccessMessage("Your offer was automatically accepted! Redirecting to checkout...")
      // Redirect to checkout with the cart_id
      setTimeout(() => {
        close()
        if (result.offer?.cart_id) {
          router.push(`/${countryCode}/checkout?cart_id=${result.offer.cart_id}`)
        } else {
          router.push(`/${countryCode}/account/offers`)
        }
      }, 1500)
      return
    } else if (result.offer?.status === "rejected") {
      setSuccessMessage("Your offer was automatically declined. The price is too low.")
    } else {
      setSuccessMessage("Your offer has been submitted! We'll review it and get back to you soon.")
    }

    // Close modal after a delay
    setTimeout(() => {
      close()
      router.push(`/${countryCode}/account/offers`)
    }, 2000)
  }

  // Calculate min/max hint
  const getHintText = () => {
    const hints: string[] = []
    if (offerConfig.min_offer_percentage) {
      const minAmount = (variantPrice * offerConfig.min_offer_percentage) / 100
      hints.push(`Min: ${convertToLocale({ amount: minAmount, currency_code: currencyCode })}`)
    }
    if (offerConfig.max_offer_percentage) {
      const maxAmount = (variantPrice * offerConfig.max_offer_percentage) / 100
      hints.push(`Max: ${convertToLocale({ amount: maxAmount, currency_code: currencyCode })}`)
    }
    return hints.join(" - ")
  }

  return (
    <>
      <Button
        onClick={handleOpenModal}
        variant="secondary"
        className="w-full h-10"
        data-testid="make-offer-button"
      >
        Make an Offer
      </Button>

      <Modal isOpen={isOpen} close={close} size="small" data-testid="make-offer-modal">
        <Modal.Title>Make an Offer</Modal.Title>
        <Modal.Body>
          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-2">
              <div className="text-small-regular text-ui-fg-subtle">
                <span className="font-semibold">{product.title}</span>
                {variant.title && variant.title !== "Default" && (
                  <span> - {variant.title}</span>
                )}
              </div>
              <div className="text-base-regular">
                Current Price:{" "}
                <span className="font-semibold">
                  {convertToLocale({ amount: variantPrice, currency_code: currencyCode })}
                </span>
              </div>
            </div>

            {error && !customer?.addresses?.length ? (
              <div className="flex flex-col gap-y-4">
                <div className="text-rose-500 text-small-regular">{error}</div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    close()
                    router.push(`/${countryCode}/account/addresses`)
                  }}
                >
                  Add Address
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-y-1">
                  <Input
                    label="Your Offer ($)"
                    name="offer_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    data-testid="offer-amount-input"
                  />
                  {getHintText() && (
                    <span className="text-ui-fg-subtle text-small-regular">
                      {getHintText()}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-y-1">
                  <label className="text-small-regular text-ui-fg-subtle">
                    Message (optional)
                  </label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full h-20 px-4 py-2 bg-ui-bg-field border border-ui-border-base rounded-md focus:outline-none focus:ring-0 focus:shadow-borders-interactive-with-active hover:bg-ui-bg-field-hover text-small-regular resize-none"
                    placeholder="Add a message to your offer..."
                    data-testid="offer-message-input"
                  />
                </div>

                {error && (
                  <div className="text-rose-500 text-small-regular">{error}</div>
                )}

                {successMessage && (
                  <div className="text-green-600 text-small-regular">{successMessage}</div>
                )}

                <Modal.Footer>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={close}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !!successMessage}
                    data-testid="submit-offer-button"
                  >
                    Submit Offer
                  </Button>
                </Modal.Footer>
              </>
            )}
          </form>
        </Modal.Body>
      </Modal>
    </>
  )
}
